<template>
  <div class="help-tooltip" ref="tooltipContainer">
    <a
      class="help-tooltip__link"
      @mouseenter="onHover"
      @mouseleave="onHoverEnd"
      @touchstart.stop="onTouchStart"
      @touchend.stop="onTouchEnd"
      @click.stop="onClickToggle"
    >
      <QuestionMarkIcon class="help-tooltip__icon" :color="helpIconColor" />
      <div
        v-if="text || image"
        class="help-tooltip__content"
        :class="contentClasses"
        :style="contentStyle"
        ref="tooltipContent"
      >
        <div class="help-tooltip__content-inner">
          <div v-if="text" class="help-tooltip__text">{{ text }}</div>
          <img
            v-if="image"
            :src="image"
            :alt="imageAlt"
            class="help-tooltip__image"
          />
        </div>
        <div class="help-tooltip__arrow" :style="arrowStyle"></div>
      </div>
    </a>
  </div>
</template>

<script>
import { QuestionMarkIcon } from '@/shared/ui/icons';

const GAP = 8;
const ARROW_SIZE = 6;
const VIEWPORT_PADDING = 8;
const MIN_SPACE = 16;

export default {
  props: {
    text: {
      type: String,
      default: '',
    },
    image: {
      type: String,
      default: '',
    },
    imageAlt: {
      type: String,
      default: 'Tooltip image',
    },
    width: {
      type: [Number, String],
      default: null,
    },
    maxWidth: {
      type: [Number, String],
      default: '90vw',
    },
    iconColor: {
      type: String,
      default: '#E4E4E7',
    },
    iconHoverColor: {
      type: String,
      default: '#000000',
    },
    preferredPosition: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
    },
  },
  data() {
    return {
      position: 'top',
      calculatedWidth: 'auto',
      isFullscreen: false,
      tooltipDimensions: { width: 0, height: 0 },
      isVisible: false,
      tooltipOffset: { top: -9999, left: -9999 },
      arrowOffset: 0,
      isTouchDevice: false,
      touchTimer: null,
      triggerRect: null,
      isHovered: false,
    };
  },
  mounted() {
    this.detectTouchDevice();
    this.calculateWidth();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handlePositioning, { passive: true });
  },
  beforeDestroy() {
    this.cleanup();
  },
  components: { QuestionMarkIcon },
  computed: {
    helpIconColor() {
      return this.isHovered ? this.iconHoverColor : this.iconColor;
    },
    contentClasses() {
      return {
        [`help-tooltip__content--${this.position}`]: !this.isFullscreen,
        'help-tooltip__content--fullscreen': this.isFullscreen,
        'help-tooltip__content--visible': this.isVisible && !this.isFullscreen,
      };
    },
    arrowStyle() {
      if (this.isFullscreen) {
        return { display: 'none' };
      }

      const isVertical = this.position === 'top' || this.position === 'bottom';
      const isHorizontal = this.position === 'left' || this.position === 'right';

      if (isVertical) {
        return {
          left: `${this.arrowOffset}px`,
          transform: 'translateX(-50%)',
        };
      }

      if (isHorizontal) {
        return {
          top: `${this.arrowOffset}px`,
          transform: 'translateY(-50%)',
        };
      }

      return {};
    },
    contentStyle() {
      if (this.isFullscreen) {
        return {
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: '0',
          left: '0',
          zIndex: '9999',
        };
      }

      const style = {
        top: this.isVisible ? `${this.tooltipOffset.top}px` : '-9999px',
        left: this.isVisible ? `${this.tooltipOffset.left}px` : '-9999px',
      };

      if (this.calculatedWidth) {
        style.width = this.calculatedWidth;
      }

      if (this.maxWidth) {
        style.maxWidth =
          typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth;
      }

      return style;
    },
  },
  methods: {
    detectTouchDevice() {
      this.isTouchDevice =
        typeof window !== 'undefined' &&
        ('ontouchstart' in window ||
          navigator.maxTouchPoints > 0 ||
          navigator.msMaxTouchPoints > 0);
    },
    calculateWidth() {
      if (this.width) {
        this.calculatedWidth =
          typeof this.width === 'number' ? `${this.width}px` : this.width;
        return;
      }

      const viewportWidth = window.innerWidth;
      if (viewportWidth < 640) {
        this.calculatedWidth = 'calc(100vw - 32px)';
      } else if (viewportWidth < 1024) {
        this.calculatedWidth = '280px';
      } else {
        this.calculatedWidth = '320px';
      }
    },
    calculateTooltipDimensions() {
      this.$nextTick(() => {
        const content = this.$refs.tooltipContent;
        if (!content) return;

        const rect = content.getBoundingClientRect();
        this.tooltipDimensions = {
          width: rect.width || 320,
          height: rect.height || 0,
        };
      });
    },
    onHover() {
      if (this.isTouchDevice) return;
      this.isHovered = true;
      this.showTooltip();
    },
    onHoverEnd() {
      if (this.isTouchDevice) return;
      this.isHovered = false;
      this.hideTooltip();
    },
    onClickToggle() {
      if (this.isTouchDevice) return;
      if (this.isVisible) {
        this.hideTooltip();
      } else {
        this.isHovered = true;
        this.showTooltip();
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('touchstart', this.handleOutsideTouch, { once: false });
          }, 100);
        });
      }
    },
    onTouchStart(event) {
      if (!this.isTouchDevice) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.isVisible) {
        this.hideTooltip();
      } else {
        this.showTooltip();
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('touchstart', this.handleOutsideTouch, { once: false });
          }, 100);
        });
      }
    },
    onTouchEnd(event) {
      if (!this.isTouchDevice) return;

      event.preventDefault();
      event.stopPropagation();
    },
    showTooltip() {
      const container = this.$refs.tooltipContainer;
      if (!container) return;

      this.triggerRect = container.getBoundingClientRect();
      this.isVisible = true;

      this.$nextTick(() => {
        this.calculateTooltipDimensions();
        this.$nextTick(() => {
          this.handlePositioning();
        });
      });
    },
    hideTooltip() {
      this.isVisible = false;
      this.isFullscreen = false;
      this.triggerRect = null;

      document.removeEventListener('touchstart', this.handleOutsideTouch);

      this.$nextTick(() => {
        this.tooltipOffset = { top: -9999, left: -9999 };
      });
    },
    handleResize() {
      this.calculateWidth();
      if (this.isVisible) {
        this.recalculateTooltip();
      }
    },
    recalculateTooltip() {
      this.calculateTooltipDimensions();
      this.$nextTick(() => {
        this.handlePositioning();
      });
    },
    handlePositioning() {
      if (!this.triggerRect || this.isFullscreen) return;

      const { width: tooltipWidth, height: tooltipHeight } = this.tooltipDimensions;

      const spaces = this.calculateAvailableSpaces(tooltipWidth, tooltipHeight);
      const needsFullscreen = this.checkNeedsFullscreen(
        tooltipWidth,
        tooltipHeight,
        spaces
      );

      if (needsFullscreen) {
        this.isFullscreen = true;
        return;
      }

      this.isFullscreen = false;
      this.position = this.determinePosition(spaces);
      this.calculateOffset(tooltipWidth, tooltipHeight);
    },
    calculateAvailableSpaces(tooltipWidth, tooltipHeight) {
      const rect = this.triggerRect;

      return {
        top: {
          available: rect.top - GAP - ARROW_SIZE - VIEWPORT_PADDING,
          needed: tooltipHeight + GAP + ARROW_SIZE,
          fits:
            rect.top >= tooltipHeight + GAP + ARROW_SIZE + VIEWPORT_PADDING,
        },
        bottom: {
          available:
            window.innerHeight - rect.bottom - GAP - ARROW_SIZE - VIEWPORT_PADDING,
          needed: tooltipHeight + GAP + ARROW_SIZE,
          fits:
            window.innerHeight - rect.bottom >=
            tooltipHeight + GAP + ARROW_SIZE + VIEWPORT_PADDING,
        },
        left: {
          available: rect.left - GAP - ARROW_SIZE - VIEWPORT_PADDING,
          needed: tooltipWidth + GAP + ARROW_SIZE,
          fits:
            rect.left >= tooltipWidth + GAP + ARROW_SIZE + VIEWPORT_PADDING,
        },
        right: {
          available:
            window.innerWidth - rect.right - GAP - ARROW_SIZE - VIEWPORT_PADDING,
          needed: tooltipWidth + GAP + ARROW_SIZE,
          fits:
            window.innerWidth - rect.right >=
            tooltipWidth + GAP + ARROW_SIZE + VIEWPORT_PADDING,
        },
      };
    },
    checkNeedsFullscreen(tooltipWidth, tooltipHeight, spaces) {
      return (
        tooltipWidth > window.innerWidth - MIN_SPACE * 2 ||
        tooltipHeight > window.innerHeight - MIN_SPACE * 2 ||
        Object.values(spaces).every((space) => !space.fits)
      );
    },
    determinePosition(spaces) {
      if (spaces[this.preferredPosition].fits) {
        return this.preferredPosition;
      }

      const feasiblePositions = Object.entries(spaces)
        .filter(([_, space]) => space.fits)
        .sort((a, b) => b[1].available - a[1].available);

      if (feasiblePositions.length > 0) {
        return feasiblePositions[0][0];
      }

      return Object.entries(spaces).reduce((best, current) =>
        current[1].available > best[1].available ? current : best
      )[0];
    },
    calculateOffset(tooltipWidth, tooltipHeight) {
      const rect = this.triggerRect;
      const isVertical = this.position === 'top' || this.position === 'bottom';
      const isHorizontal = this.position === 'left' || this.position === 'right';

      const offsets = {
        top: {
          top: rect.top - tooltipHeight - GAP - ARROW_SIZE,
          left: rect.left + rect.width / 2 - tooltipWidth / 2,
        },
        bottom: {
          top: rect.bottom + GAP + ARROW_SIZE,
          left: rect.left + rect.width / 2 - tooltipWidth / 2,
        },
        left: {
          top: rect.top + rect.height / 2 - tooltipHeight / 2,
          left: rect.left - tooltipWidth - GAP - ARROW_SIZE,
        },
        right: {
          top: rect.top + rect.height / 2 - tooltipHeight / 2,
          left: rect.right + GAP + ARROW_SIZE,
        },
      };

      const offset = offsets[this.position];

      offset.left = Math.max(
        VIEWPORT_PADDING,
        Math.min(offset.left, window.innerWidth - tooltipWidth - VIEWPORT_PADDING)
      );

      offset.top = Math.max(VIEWPORT_PADDING, offset.top);

      this.tooltipOffset = {
        top: offset.top,
        left: offset.left,
      };

      if (isVertical) {
        const triggerCenter = rect.left + rect.width / 2;
        this.arrowOffset = triggerCenter - offset.left;
        this.arrowOffset = Math.max(
          ARROW_SIZE * 2,
          Math.min(this.arrowOffset, tooltipWidth - ARROW_SIZE * 2)
        );
      }

      if (isHorizontal) {
        const triggerCenter = rect.top + rect.height / 2;
        this.arrowOffset = triggerCenter - offset.top;
        this.arrowOffset = Math.max(
          ARROW_SIZE * 2,
          Math.min(this.arrowOffset, tooltipHeight - ARROW_SIZE * 2)
        );
      }
    },
    closeFullscreen() {
      this.isFullscreen = false;
      this.$nextTick(() => {
        this.handlePositioning();
      });
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isFullscreen) {
        this.closeFullscreen();
      }
    },
    handleOutsideClick(event) {
      if (
        this.isFullscreen &&
        this.$refs.tooltipContent &&
        !this.$refs.tooltipContent.contains(event.target)
      ) {
        this.closeFullscreen();
      }
    },
    handleOutsideTouch(event) {
      const tooltipContent = this.$refs.tooltipContent;
      const tooltipContainer = this.$refs.tooltipContainer;

      if (
        tooltipContent &&
        tooltipContainer &&
        !tooltipContent.contains(event.target) &&
        !tooltipContainer.contains(event.target)
      ) {
        this.hideTooltip();
      }
    },
    addFullscreenListeners() {
      document.addEventListener('keydown', this.handleEscapeKey);
      document.addEventListener('click', this.handleOutsideClick);
    },
    removeFullscreenListeners() {
      document.removeEventListener('keydown', this.handleEscapeKey);
      document.removeEventListener('click', this.handleOutsideClick);
    },
    clearTouchTimer() {
      if (this.touchTimer) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
      }
    },
    cleanup() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handlePositioning);
      document.removeEventListener('touchstart', this.handleOutsideTouch);
      this.removeFullscreenListeners();
      this.clearTouchTimer();
    },
  },
  watch: {
    width() {
      this.calculateWidth();
      if (this.isVisible) {
        this.recalculateTooltip();
      }
    },
    text() {
      if (this.isVisible) {
        this.recalculateTooltip();
      }
    },
    image() {
      if (this.isVisible) {
        this.recalculateTooltip();
      }
    },
    isFullscreen(newVal) {
      if (newVal) {
        this.addFullscreenListeners();
      } else {
        this.removeFullscreenListeners();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.help-tooltip {
  position: relative;
  display: inline-flex;

  &__link {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  &__icon {
    transition: opacity 0.2s ease;
  }

  &__content {
    position: fixed;
    padding: 0;
    background-color: transparent;
    border-radius: 8px;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    pointer-events: none;
    z-index: 1000;
    max-height: 80vh;
    overflow: visible;
    box-shadow: none;

    &--visible {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }

    &--fullscreen {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: auto !important;
      background-color: rgba(0, 0, 0, 0.8) !important;
      display: flex !important;
      align-items: center;
      justify-content: center;
      border-radius: 0 !important;
      cursor: pointer;

      .help-tooltip__content-inner {
        cursor: auto;
      }

      .help-tooltip__arrow {
        display: none;
      }
    }
  }

  &__content-inner {
    background-color: #101828;
    color: #fdfdfd;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    max-height: 80vh;
    overflow-y: auto;
    position: relative;

    .help-tooltip__content--fullscreen & {
      max-width: 90%;
      max-height: 90%;
      padding: 20px;
      font-size: 14px;

      &::after {
        content: '×';
        position: absolute;
        top: 12px;
        right: 16px;
        color: #fdfdfd;
        font-size: 24px;
        cursor: pointer;
        line-height: 1;
        z-index: 10001;
      }
    }
  }

  &__arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;

    .help-tooltip__content--top & {
      bottom: -6px;
      border-width: 6px 6px 0 6px;
      border-color: #101828 transparent transparent transparent;
    }

    .help-tooltip__content--bottom & {
      top: -6px;
      border-width: 0 6px 6px 6px;
      border-color: transparent transparent #101828 transparent;
    }

    .help-tooltip__content--left & {
      right: -6px;
      border-width: 6px 0 6px 6px;
      border-color: transparent transparent transparent #101828;
    }

    .help-tooltip__content--right & {
      left: -6px;
      border-width: 6px 6px 6px 0;
      border-color: transparent #101828 transparent transparent;
    }
  }

  &__text {
    word-break: break-word;
  }

  &__image {
    max-width: 100%;
    height: auto;
    border-radius: 2px;
    display: block;
  }
}

.help-tooltip__content--fullscreen .help-tooltip__content-inner {
  scrollbar-width: thin;
  scrollbar-color: #666 #101828;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #101828;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #666;
    border-radius: 3px;
  }
}
</style>
